import { useQuery } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Outlet } from '@tanstack/react-router'
import { isThisWeek, isToday, isYesterday, subDays } from 'date-fns'
import { type JSX, useMemo } from 'react'
import Link from '~/components/link/Link'
import { relativeDate } from '~/helpers/dateHelper'
import { RouterOutput, useTRPC } from '~/services/trpc'

import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@hap/components'

import { PIVOT_FIELDS } from './reports/pivot/-components/pivot-options'

export const Route = createFileRoute('/_authenticated/$alias/_app/reports')({
  component: RouteComponent,
})

function RouteComponent() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { mutateAsync: deleteQuery } = useMutation(
    trpc.queries.delete.mutationOptions({
      async onMutate({ queryId }) {
        await queryClient.cancelQueries(trpc.queries.history.pathFilter())
        const previousQueries = queryClient.getQueryData(trpc.queries.history.queryKey())
        queryClient.setQueryData(
          trpc.queries.history.queryKey({ type: 'pivot' }),
          (prev) => {
            if (!prev) return prev
            return prev.filter((q) => q.id !== queryId)
          },
        )
        return { previousQueries }
      },
    }),
  )
  const { data: queries } = useQuery(trpc.queries.history.queryOptions({ type: 'pivot' }))
  const groupedQueries = useMemo(() => groupQueries(queries), [queries])

  return (
    <div className="flex h-[calc(100vh-63px)] items-stretch">
      <Tabs
        defaultValue="recent"
        className="flex h-auto w-[300px] shrink-0 flex-col border-r"
      >
        <TabsList className="gutters h-auto w-full shrink-0 rounded-none border-b pt-2 pb-0">
          <TabsTrigger
            className="border-b-border/50 -mb-px -ml-px rounded-none border text-base data-[state=active]:border-t-black data-[state=active]:bg-transparent"
            value="saved"
          >
            Saved
          </TabsTrigger>
          <TabsTrigger
            className="border-b-border/50 -mb-px -ml-px rounded-none border text-base data-[state=active]:border-t-black data-[state=active]:bg-transparent"
            value="recent"
          >
            Recent
          </TabsTrigger>
        </TabsList>
        <div className="shrink grow overflow-y-auto">
          <TabsContent value="saved">
            <SavedQueries />
          </TabsContent>
          <TabsContent value="recent">
            {Object.entries(groupedQueries).map(([key, queries]) => {
              return queries.length ? (
                <div key={key}>
                  <h4 className="text-muted-foreground gutters">{key}</h4>
                  <ul className="space-y-4">
                    {queries?.map((q) => {
                      const params = q.params as Record<string, { field: string }[]>
                      return (
                        <Link
                          to={'?qid=' + q.id}
                          key={q.id}
                          className="gutters group flex items-center"
                        >
                          <div className="shrink grow">
                            {relativeDate(q.createdAt)}
                            <br />
                            Breakdown{' '}
                            {humanizePivotFields(
                              params?.rows as { field: string }[],
                            )} by{' '}
                            {humanizePivotFields(params?.columns as { field: string }[])}
                          </div>
                          <Button
                            icon="trash"
                            variant="ghost"
                            size="icon-sm"
                            className="shrink-0 opacity-0 group-hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation()
                              e.preventDefault()
                              deleteQuery({ queryId: q.id })
                            }}
                          />
                        </Link>
                      )
                    })}
                  </ul>
                </div>
              ) : null
            })}
          </TabsContent>
        </div>
      </Tabs>
      <div className="w-[calc(100vw-300px)] shrink">
        <Outlet />
      </div>
    </div>
  )
}

function humanizePivotFields(params: { field: string }[]) {
  return params
    ?.map((r) => PIVOT_FIELDS.find((f) => f.field === r.field)?.alias)
    .join(', ')
}

function groupQueries(items?: RouterOutput['queries']['history']) {
  const grouped: GroupedData = {
    'Today': [],
    'Yesterday': [],
    'This Week': [],
    'Less than 7 days': [],
    'Older than 7 days': [],
    'Older than 30 days': [],
  }
  if (!items) return {}

  const now = new Date()
  const sevenDaysAgo = subDays(now, 7)
  const thirtyDaysAgo = subDays(now, 30)

  for (const item of items) {
    const lastUsedDate = new Date(item.updatedAt)
    if (lastUsedDate < thirtyDaysAgo) {
      grouped['Older than 30 days'].push(item)
    } else if (lastUsedDate < sevenDaysAgo) {
      grouped['Older than 7 days'].push(item)
    } else if (isYesterday(lastUsedDate)) {
      grouped['Yesterday'].push(item)
    } else if (isToday(lastUsedDate)) {
      grouped['Today'].push(item)
    } else if (isThisWeek(lastUsedDate)) {
      grouped['This Week'].push(item)
    } else {
      grouped['Less than 7 days'].push(item)
    }
  }

  return grouped
}

interface GroupedData {
  [key: string]: RouterOutput['queries']['history']
}

function SavedQueries(): JSX.Element {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { data: queries } = useQuery(trpc.queries.saved.queryOptions({ type: 'pivot' }))
  const { mutateAsync: deleteQuery } = useMutation(
    trpc.queries.delete.mutationOptions({
      async onMutate({ queryId }) {
        await queryClient.cancelQueries(trpc.queries.history.pathFilter())
        const previousQueries = queryClient.getQueryData(trpc.queries.history.queryKey())
        queryClient.setQueryData(
          trpc.queries.history.queryKey({ type: 'pivot' }),
          (prev) => {
            if (!prev) return prev
            return prev.filter((q) => q.id !== queryId)
          },
        )
        return { previousQueries }
      },
    }),
  )
  return (
    <div>
      {queries?.map((q) => {
        return (
          <Link
            to={'?qid=' + q.id}
            key={q.id}
            className="gutters group flex items-center"
          >
            <div className="shrink grow">
              {relativeDate(q.createdAt)}
              <br />
              {q.name}
            </div>
            <Button
              icon="trash"
              variant="ghost"
              size="icon-sm"
              className="shrink-0 opacity-0 group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                deleteQuery({ queryId: q.id })
              }}
            />
          </Link>
        )
      })}
    </div>
  )
}
